import entityManager from '../lib/entityManager';
import { GameEvents } from '../lib/GameEvents';
import { HexPosition, Renderable, IsTerrain, PredictedState, TakesTurns, PlayerControlled, IntendsToAct, Dead, ActorStats } from '../components';

/**
 * SpatialSystem is the bridge between ECS entity data and UI state.
 * It queries entities and emits spatial events that the UI layer consumes.
 */
export default class SpatialSystem {
  /**
   * @param {EventEmitter} eventEmitter Event bus for emitting spatial updates.
   */
  constructor(eventEmitter) {
    this.eventEmitter = eventEmitter;
    this._syncTerrain();
    this.sync();
  }

  sync() {
    this._syncCharacters();
    this._syncIntentions();
    // we don't re-sync terrain because at this point it never changes. Moving/changing terrain? Someday? Maybe?
  }


  /**
   * Sync enemy intention indicators to the UI state slice.
   */
  _syncIntentions() {
    const enemies = entityManager.query([IntendsToAct, HexPosition])
      .filter(e => !e.hasComponent(PlayerControlled));

    const indicators = enemies.map(entity => {
      const intent = entity.getComponent(IntendsToAct);
      const position = entity.getComponent(HexPosition);

      return {
        entityId: entity.id,
        position: { q: position.coords.q, r: position.coords.r },
        bearing: position.bearing,
        actionType: intent.actionType,
        details: {
          coords: intent.details.coords ? { q: intent.details.coords.q, r: intent.details.coords.r } : undefined,
          bearing: intent.details.bearing,
          targetId: intent.details.targetId,
          sourceComponentName: intent.sourceComponentName,
        },
      };
    });

    this.eventEmitter.emit(GameEvents.UPDATE_INTENTION_INDICATORS, { indicators });
  }

  /**
   * Sync terrain state to the UI state slice.
   */
  _syncTerrain() {
    const terrainEntities = entityManager.query([IsTerrain, HexPosition, Renderable]);
    const terrain = terrainEntities.map(entity => {
      const position = entity.getComponent(HexPosition);
      const renderable = entity.getComponent(Renderable);
      return {
        entityId: entity.id,
        coords: { q: position.coords.q, r: position.coords.r },
        spriteKey: renderable.spriteKey,
      };
    });
    this.eventEmitter.emit(GameEvents.UPDATE_TERRAIN, { terrain });
  }

  /**
   * Sync character state to the UI state slice.
   */
  _syncCharacters() {
    const actors = entityManager.query([HexPosition, Renderable])
      .filter(e => !e.hasComponent(IsTerrain));

    const characters = actors.map(entity => {
      const position = entity.getComponent(HexPosition);
      const renderable = entity.getComponent(Renderable);
      const takesTurns = entity.getComponent(TakesTurns);
      const predictedState = entity.getComponent(PredictedState);
      const stats = entity.getComponent(ActorStats);
      const isPlayer = entity.hasComponent(PlayerControlled);

      return {
        entityId: entity.id,
        coords: { q: position.coords.q, r: position.coords.r },
        bearing: position.bearing,
        spriteKey: renderable.spriteKey,
        scale: renderable.scale,
        isDead: entity.hasComponent(Dead),
        isPlayer,
        energy: takesTurns ? takesTurns.energy : null,
        predictedEnergy: predictedState ? predictedState.totalPredictedEnergy : null,
        speed: stats ? stats.speed : null,
      };
    });

    this.eventEmitter.emit(GameEvents.UPDATE_CHARACTERS, { characters });
  }

}
